import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import { FormField } from 'components/FormField/FormField';
import {
  MultiTextInput,
  type MultiTextInputProps,
} from 'components/MultiTextInput/MultiTextInput';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode, RefObject } from 'react';
import {
  type FormikValueAdapter,
  applyFormikValueAdapter,
} from 'utils/formik/FormikValueAdapter';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type Props<TFormikValues> = Omit<MultiTextInputProps, 'value' | 'onChange'> & {
  autoFocus?: boolean;
  formik: FormikProps<TFormikValues>;
  inputRef?: RefObject<HTMLInputElement>;
  name: StringKey<TFormikValues> | NestedFormikName;
  onChange?: (value: string[]) => void;
  label?: ReactNode;
  transformValue?: FormikValueAdapter<string[]>;
};

export const FormikMultiTextField = <TFormikValues,>({
  formik,
  inputRef,
  label,
  onChange,
  transformValue,
  ...props
}: Props<TFormikValues>): JSX.Element => {
  const field = formik.getFieldProps(props.name);
  const { error } = formik.getFieldMeta(props.name as string);
  const hasError = fieldHasError<TFormikValues>(formik, props.name);
  const transformedValueController = applyFormikValueAdapter(
    field,
    transformValue,
  );

  const handleChange = (value: string[]) => {
    transformedValueController.onChange({
      target: {
        name: field.name,
        value,
      },
    });
    onChange?.(value);
  };

  const handleBlur = () => {
    field.onBlur({
      target: {
        name: field.name,
      },
    });
  };

  return (
    <FormField
      className={classNames(hasError && 'formik-error-scroll-anchor')}
      label={label}
      error={hasError ? error : undefined}
    >
      <MultiTextInput
        {...field}
        {...props}
        hasError={hasError}
        inputRef={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        value={transformedValueController.value}
      />
    </FormField>
  );
};
